import { useCallback, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import {
  createLeague as createLeagueApi,
  joinLeague as joinLeagueApi,
  leaveLeague as leaveLeagueApi,
  inviteToLeague as inviteToLeagueApi,
  updateLeagueMotto,
} from '../api/leagues'
import { ApiError } from '../api/client'
import type { League, LeagueJoinPolicy } from '../types'

export interface UseLeaguesOptions {
  onToast: (message: string) => void
  /** Push the league detail screen — App owns `selectedLeagueId` because the shared-element
   *  transition straddles the list→detail render swap. */
  onOpenLeague: (leagueId: string) => void
  /** Pop back off the detail screen after leaving a league. */
  onCloseLeague: () => void
}

export interface LeaguesController {
  leagues: League[]
  /** Exposed because the initial load, the phase poll, the dev tools and the identity handlers
   *  all refresh leagues as a side effect of something else. */
  setLeagues: Dispatch<SetStateAction<League[]>>
  /** Counter bumped by every `setLeagues` call, i.e. by every local mutation. A
   *  background refresh captures it before its request and re-reads it after: a change means a
   *  mutation landed while the request was in the air, and the fetched list is the older truth. */
  readMutationToken: () => number
  isCreatingLeague: boolean
  isJoiningLeague: boolean
  openCreateLeague: () => void
  closeCreateLeague: () => void
  openJoinLeague: () => void
  closeJoinLeague: () => void
  createLeague: (name: string, motto: string, joinPolicy: LeagueJoinPolicy) => Promise<void>
  joinLeague: (inviteCode: string) => Promise<void>
  leaveLeague: (leagueId: string) => Promise<void>
  saveMotto: (leagueId: string, motto: string) => Promise<void>
  inviteToLeague: (leagueId: string, userId: string) => Promise<void>
}

/**
 * Mini-league membership + the two entry sheets. Every handler patches the
 * local list from the server's response rather than refetching, so the list stays correct without
 * a round trip; the failure copy is per-handler and, for join/invite, per HTTP status.
 */
export function useLeagues({
  onToast,
  onOpenLeague,
  onCloseLeague,
}: UseLeaguesOptions): LeaguesController {
  const [leagues, setLeaguesState] = useState<League[]>([])
  const [isCreatingLeague, setIsCreatingLeague] = useState(false)
  const [isJoiningLeague, setIsJoiningLeague] = useState(false)

  // Every write to the list goes through here, which is what makes a single counter enough to
  // detect "something changed the list while my request was running".
  const mutationTokenRef = useRef(0)
  const setLeagues = useCallback<Dispatch<SetStateAction<League[]>>>((value) => {
    mutationTokenRef.current += 1
    setLeaguesState(value)
  }, [])
  const readMutationToken = useCallback(() => mutationTokenRef.current, [])

  return {
    leagues,
    setLeagues,
    readMutationToken,
    isCreatingLeague,
    isJoiningLeague,
    openCreateLeague: () => setIsCreatingLeague(true),
    closeCreateLeague: () => setIsCreatingLeague(false),
    openJoinLeague: () => setIsJoiningLeague(true),
    closeJoinLeague: () => setIsJoiningLeague(false),

    createLeague: async (name, motto, joinPolicy) => {
      try {
        const league = await createLeagueApi(name, motto, joinPolicy)
        setLeagues((prev) => [...prev, league])
        setIsCreatingLeague(false)
        onOpenLeague(league.id)
        onToast(`League created! Invite code: ${league.inviteCode}`)
      } catch {
        onToast('Failed to create league. Please try again.')
      }
    },

    joinLeague: async (inviteCode) => {
      try {
        const league = await joinLeagueApi(inviteCode)
        // Replace rather than append: the club/global leagues you are auto-joined into can
        // already be in the list.
        setLeagues((prev) => [...prev.filter((l) => l.id !== league.id), league])
        setIsJoiningLeague(false)
        onOpenLeague(league.id)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          onToast('Invite code not found.')
        } else if (err instanceof ApiError && err.status === 409) {
          onToast('You are already a member of this league.')
        } else {
          onToast('Failed to join league. Please try again.')
        }
      }
    },

    leaveLeague: async (leagueId) => {
      try {
        await leaveLeagueApi(leagueId)
        setLeagues((prev) => prev.filter((l) => l.id !== leagueId))
        onCloseLeague()
      } catch {
        onToast('Failed to leave league. Please try again.')
      }
    },

    saveMotto: async (leagueId, motto) => {
      try {
        const league = await updateLeagueMotto(leagueId, motto)
        setLeagues((prev) => prev.map((l) => (l.id === leagueId ? league : l)))
        onToast(motto.trim() ? 'Saved.' : 'Cleared.')
      } catch {
        onToast("Couldn't save that. Please try again.")
      }
    },

    inviteToLeague: async (leagueId, userId) => {
      try {
        const league = await inviteToLeagueApi(leagueId, userId)
        setLeagues((prev) => prev.map((l) => (l.id === leagueId ? league : l)))
        onToast('Player added to the league.')
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          onToast('They are already in this league.')
        } else {
          onToast('Failed to invite that player. Please try again.')
        }
      }
    },
  }
}
